import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { TableMeta } from '../services/api';
import './OperationsPanel.css';

interface Props { tables: TableMeta[]; }

type OpType = 'aggregate' | 'filter' | 'sort' | 'groupby' | 'duplicates' | 'describe';

interface ResultData {
  type: string;
  data?: unknown;
  columns?: string[];
  rows?: number;
  result?: unknown;
  function?: string;
  column?: string;
  duplicate_count?: number;
  description?: Record<string, unknown>;
  shape?: { rows: number; columns: number };
}

export default function OperationsPanel({ tables }: Props) {
  const api = useApi();
  const [op, setOp] = useState<OpType>('aggregate');
  const [tableId, setTableId] = useState(tables[0]?.table_id || '');
  const [column, setColumn] = useState('');
  const [func, setFunc] = useState('sum');
  const [operator, setOperator] = useState('eq');
  const [filterValue, setFilterValue] = useState('');
  const [ascending, setAscending] = useState(true);
  const [groupByCols, setGroupByCols] = useState('');
  const [aggFunc, setAggFunc] = useState('sum');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const [error, setError] = useState('');

  const selectedTable = tables.find(t => t.table_id === tableId);
  const cols = selectedTable?.headers || [];
  const numericCols = selectedTable?.columns?.filter(c => c.data_type === 'numeric').map(c => c.column_name) || [];

  // Auto-select first numeric column for aggregate/groupby ops
  React.useEffect(() => {
    if (['aggregate', 'groupby'].includes(op) && numericCols.length > 0 && (!column || !numericCols.includes(column))) {
      setColumn(numericCols[0]);
    } else if (!column && cols.length > 0) {
      setColumn(cols[0]);
    }
  }, [tableId, op]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      let res;
      if (op === 'aggregate') {
        res = await api.post('/operations/aggregate', { table_id: tableId, column, function: func });
      } else if (op === 'filter') {
        res = await api.post('/operations/filter', { table_id: tableId, column, operator, value: filterValue });
      } else if (op === 'sort') {
        res = await api.post('/operations/sort', { table_id: tableId, column, ascending });
      } else if (op === 'groupby') {
        res = await api.post('/operations/groupby', {
          table_id: tableId,
          group_by: groupByCols.split(',').map(s => s.trim()).filter(Boolean),
          agg_column: column,
          agg_func: aggFunc,
        });
      } else if (op === 'duplicates') {
        res = await api.post('/operations/duplicates', { table_id: tableId });
      } else if (op === 'describe') {
        res = await api.get(`/operations/describe/${tableId}`);
      }
      setResult(res?.data || null);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    if (!result) return;
    if (result.type === 'dataframe' && result.columns) {
      const rows = result.data as Record<string, unknown>[];
      const cols = result.columns;
      const header = cols.join('\t');
      const body = rows.map(r => cols.map(c => String(r[c] ?? '')).join('\t')).join('\n');
      navigator.clipboard.writeText(`${header}\n${body}`);
    } else if (result.type === 'scalar') {
      navigator.clipboard.writeText(String(result.result));
    }
  };

  const renderResult = () => {
    if (!result) return null;
    if (result.type === 'scalar') {
      return (
        <div className="ops-result-scalar">
          <span className="ops-result-label">{result.function?.toUpperCase()} of {result.column}</span>
          <span className="ops-result-value">{String(result.result)}</span>
        </div>
      );
    }
    if (result.type === 'dataframe') {
      const rows = result.data as Record<string, unknown>[];
      const cols = result.columns || [];
      return (
        <div className="ops-result-table-wrap">
          {result.duplicate_count !== undefined && (
            <p className="ops-result-note">{result.duplicate_count} duplicate rows found</p>
          )}
          <table className="ops-result-table">
            <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.slice(0, 50).map((row, i) => (
                <tr key={i}>{cols.map(c => <td key={c}>{String(row[c] ?? '')}</td>)}</tr>
              ))}
            </tbody>
          </table>
          {rows.length > 50 && <p className="ops-result-note">Showing 50 of {rows.length} rows</p>}
        </div>
      );
    }
    if (result.description) {
      return (
        <div className="ops-result-describe">
          <p className="ops-result-note">Shape: {result.shape?.rows} rows × {result.shape?.columns} columns</p>
          <pre className="ops-result-pre">{JSON.stringify(result.description, null, 2)}</pre>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="ops-panel">
      <div className="ops-controls card">
        <div className="ops-row">
          <label>Table</label>
          <select value={tableId} onChange={e => { setTableId(e.target.value); setColumn(''); }}>
            {tables.map(t => (
              <option key={t.table_id} value={t.table_id}>
                {t.start_col}{t.start_row}:{t.end_col}{t.end_row} — {t.headers.slice(0, 3).join(', ')}{t.headers.length > 3 ? '…' : ''} ({t.row_count} rows)
              </option>
            ))}
          </select>
        </div>

        <div className="ops-row">
          <label>Operation</label>
          <div className="ops-op-tabs">
            {OPS.map(o => (
              <button key={o.id} className={`ops-op-tab ${op === o.id ? 'active' : ''}`} onClick={() => setOp(o.id as OpType)}>
                {o.icon} {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Column picker */}
        {['aggregate', 'filter', 'sort', 'groupby'].includes(op) && (
          <div className="ops-row">
            <label>{op === 'groupby' ? 'Aggregate column' : 'Column'}</label>
            <select value={column} onChange={e => setColumn(e.target.value)}>
              <option value="">Select column...</option>
              {cols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {op === 'aggregate' && (
          <div className="ops-row">
            <label>Function</label>
            <select value={func} onChange={e => setFunc(e.target.value)}>
              {['sum', 'mean', 'min', 'max', 'count', 'std'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        )}

        {op === 'filter' && (
          <>
            <div className="ops-row">
              <label>Operator</label>
              <select value={operator} onChange={e => setOperator(e.target.value)}>
                {[['eq','='],['ne','≠'],['gt','>'],['lt','<'],['gte','≥'],['lte','≤'],['contains','contains'],['startswith','starts with']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="ops-row">
              <label>Value</label>
              <input value={filterValue} onChange={e => setFilterValue(e.target.value)} placeholder="Filter value..." />
            </div>
          </>
        )}

        {op === 'sort' && (
          <div className="ops-row">
            <label>Direction</label>
            <select value={ascending ? 'asc' : 'desc'} onChange={e => setAscending(e.target.value === 'asc')}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        )}

        {op === 'groupby' && (
          <>
            <div className="ops-row">
              <label>Group by (comma-separated)</label>
              <input value={groupByCols} onChange={e => setGroupByCols(e.target.value)} placeholder="col1, col2..." />
            </div>
            <div className="ops-row">
              <label>Aggregation</label>
              <select value={aggFunc} onChange={e => setAggFunc(e.target.value)}>
                {['sum', 'mean', 'min', 'max', 'count'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </>
        )}

        <button className="btn btn-primary ops-run" onClick={run} disabled={loading}>
          {loading ? <><span className="spinner" /> Running...</> : '▶ Run'}
        </button>
        {error && <p className="ops-error">{error}</p>}
      </div>

      <div className="ops-result">
        {!result && !loading && (
          <div className="ops-result-empty">Configure an operation and click Run</div>
        )}
        {loading && <div className="ops-result-empty"><span className="spinner" /> Running...</div>}
        {result && (
          <div>
            <div className="ops-result-actions">
              <button className="btn btn-ghost" onClick={copyResult} title="Copy to clipboard">📋 Copy</button>
            </div>
            {renderResult()}
          </div>
        )}
      </div>
    </div>
  );
}

const OPS = [
  { id: 'aggregate', icon: '∑', label: 'Aggregate' },
  { id: 'filter', icon: '⊃', label: 'Filter' },
  { id: 'sort', icon: '↕', label: 'Sort' },
  { id: 'groupby', icon: '⊞', label: 'Group By' },
  { id: 'duplicates', icon: '⊕', label: 'Duplicates' },
  { id: 'describe', icon: 'ℹ', label: 'Describe' },
];
